import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Grid, TextField, Button, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Autocomplete, Divider, Chip
} from '@mui/material';
import { Print as PrintIcon, Add as AddIcon } from '@mui/icons-material';
import { studentAPI, feeAPI, schoolSettingsAPI, academicYearAPI, challanAPI } from '../services/api';
import { toast } from 'react-toastify';

const PaymentChallan = () => {
    const printRef = useRef();
    const [students, setStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [academicYears, setAcademicYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [generatedChallan, setGeneratedChallan] = useState(null);

    // Fee form state
    const [formData, setFormData] = useState({
        paymentMode: 'cash',
        bankAccountNo: '',
        bankName: '',
        tuitionFee: '',
        termFee: '',
        processingFee: '',
        admissionFee: '',
        otherFee: ''
    });

    useEffect(() => {
        fetchAcademicYears();
        fetchBankSettings();
    }, []);

    const fetchBankSettings = async () => {
        try {
            const res = await schoolSettingsAPI.getBank();
            if (res.data.success) {
                setFormData(prev => ({
                    ...prev,
                    bankAccountNo: res.data.data.bankAccountNo || '0307288372',
                    bankName: res.data.data.bankName || 'VP Super English Med. School, Supe'
                }));
            }
        } catch (error) {
            console.error('Error fetching bank settings:', error);
            setFormData(prev => ({
                ...prev,
                bankAccountNo: '0307288372',
                bankName: 'VP Super English Med. School, Supe'
            }));
        }
    };

    const fetchAcademicYears = async () => {
        try {
            const res = await academicYearAPI.getAll();
            if (res.data.success) {
                setAcademicYears(res.data.data);
                const current = res.data.data.find(y => y.isCurrent);
                if (current) setSelectedYear(current.id);
            }
        } catch (error) {
            console.error('Error fetching academic years:', error);
        }
    };

    const handleStudentSearch = async (searchValue) => {
        if (!searchValue || searchValue.length < 2) return;
        setSearchLoading(true);
        try {
            const res = await studentAPI.getAll({ search: searchValue });
            setStudents(res.data.data || []);
        } catch (error) {
            console.error('Error searching students:', error);
        } finally {
            setSearchLoading(false);
        }
    };

    const calculateTotal = () => {
        const fees = [formData.tuitionFee, formData.termFee, formData.processingFee, formData.admissionFee, formData.otherFee];
        return fees.reduce((sum, fee) => sum + (parseFloat(fee) || 0), 0);
    };

    const handleGenerateChallan = async () => {
        if (!selectedStudent) {
            toast.error('Please select a student');
            return;
        }
        if (calculateTotal() <= 0) {
            toast.error('Please enter at least one fee amount');
            return;
        }

        try {
            const res = await challanAPI.generate({
                studentId: selectedStudent.id,
                academicYearId: selectedYear,
                ...formData
            });
            if (res.data.success) {
                setGeneratedChallan(res.data.data);
                toast.success('Challan generated successfully!');
            } else {
                toast.error(res.data.error);
            }
        } catch (error) {
            toast.error('Error generating challan');
        }
    };

    const handlePrint = () => {
        const printContent = printRef.current;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
            <head>
                <title>Payment Challan - ${generatedChallan?.challanNo}</title>
                <style>
                    @page { size: A4 landscape; margin: 5mm; }
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: Arial, sans-serif; font-size: 10px; }
                    .challan-container { display: flex; width: 100%; }
                    .challan-copy { flex: 1; border: 1px solid #000; padding: 5px; margin: 2px; }
                    .copy-header { text-align: center; font-weight: bold; border-bottom: 1px solid #000; padding-bottom: 3px; margin-bottom: 5px; }
                    .school-name { font-size: 11px; font-weight: bold; text-align: center; }
                    .bank-info { font-size: 9px; text-align: center; margin-bottom: 5px; }
                    .info-row { display: flex; justify-content: space-between; margin: 2px 0; font-size: 9px; }
                    .info-label { font-weight: bold; }
                    .fee-table { width: 100%; border-collapse: collapse; margin: 5px 0; font-size: 9px; }
                    .fee-table td { border: 1px solid #000; padding: 2px 4px; }
                    .total-row { font-weight: bold; background: #f0f0f0; }
                    .amount-words { font-size: 8px; font-style: italic; margin: 3px 0; }
                    .signature-row { display: flex; justify-content: space-between; margin-top: 15px; font-size: 8px; }
                    .signature-box { text-align: center; }
                    .denomination { float: right; font-size: 8px; }
                    .denom-table { font-size: 8px; border-collapse: collapse; }
                    .denom-table td { border: 1px solid #000; padding: 1px 3px; }
                </style>
            </head>
            <body>
                ${printContent.innerHTML}
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const formatDate = (date) => {
        const d = new Date(date);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };

    const ChallanCopy = ({ title, challan, showDenomination }) => (
        <div className="challan-copy">
            <div className="copy-header">{title}</div>
            <div className="school-name">{formData.bankName}</div>
            <div className="bank-info">Bank A/c No- {formData.bankAccountNo}</div>

            <div className="info-row">
                <span><span className="info-label">Challan No.:</span> {challan.challanNo}</span>
            </div>
            <div className="info-row">
                <span><span className="info-label">Date:</span> {formatDate(challan.challanDate)}</span>
            </div>
            <div className="info-row">
                <span><span className="info-label">Name:</span> {challan.student?.user?.firstName} {challan.student?.user?.lastName}</span>
            </div>
            <div className="info-row">
                <span><span className="info-label">Class:</span> {challan.student?.class?.name} {challan.student?.section?.name}</span>
            </div>
            <div className="info-row">
                <span><span className="info-label">Mobile No:</span> {challan.student?.user?.phone}</span>
            </div>
            <div className="info-row">
                <span><span className="info-label">Mode of Payment:</span> {challan.paymentMode}</span>
            </div>
            <div className="info-row">
                <span><span className="info-label">Year:</span> {challan.academicYear?.name}</span>
            </div>
            <div className="info-row">
                <span><span className="info-label">Amount Deposited Rs:</span> {challan.depositedAmount}</span>
            </div>

            <table className="fee-table">
                <tbody>
                    <tr><td>Tuition Fee</td><td style={{ textAlign: 'right' }}>{challan.tuitionFee || 0}</td></tr>
                    <tr><td>Term Fee</td><td style={{ textAlign: 'right' }}>{challan.termFee || 0}</td></tr>
                    <tr><td>Processing/Registration</td><td style={{ textAlign: 'right' }}>{challan.processingFee || 0}</td></tr>
                    <tr><td>Admission Fee</td><td style={{ textAlign: 'right' }}>{challan.admissionFee || 0}</td></tr>
                    <tr className="total-row"><td>Total Rs.</td><td style={{ textAlign: 'right' }}>{challan.totalAmount}</td></tr>
                </tbody>
            </table>

            <div className="amount-words">In Words: Rs. {challan.amountInWords}</div>

            {showDenomination && (
                <table className="denom-table" style={{ float: 'right' }}>
                    <tbody>
                        <tr><td>Coins</td><td></td></tr>
                        <tr><td>x 0010</td><td></td></tr>
                        <tr><td>x 0020</td><td></td></tr>
                        <tr><td>x 0050</td><td></td></tr>
                        <tr><td>x 0100</td><td></td></tr>
                        <tr><td>x 0500</td><td></td></tr>
                        <tr><td>x 2000</td><td></td></tr>
                    </tbody>
                </table>
            )}

            <div className="signature-row">
                <div className="signature-box">
                    <div>_____________</div>
                    <div>Signature of the Depositor</div>
                </div>
                <div className="signature-box">
                    <div>_____________</div>
                    <div>School and Bank Seal & Signature</div>
                </div>
            </div>
        </div>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Payment Challan</Typography>

            {!generatedChallan ? (
                <Paper sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        {/* Student Search */}
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                options={students}
                                getOptionLabel={(option) =>
                                    `${option.user?.firstName} ${option.user?.lastName} - ${option.admissionNumber} (${option.class?.name})`
                                }
                                loading={searchLoading}
                                onInputChange={(e, value) => handleStudentSearch(value)}
                                onChange={(e, value) => setSelectedStudent(value)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Search Student" placeholder="Enter name or admission no" />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                select fullWidth label="Academic Year"
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                            >
                                {academicYears.map(y => (
                                    <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                select fullWidth label="Payment Mode"
                                value={formData.paymentMode}
                                onChange={(e) => setFormData({ ...formData, paymentMode: e.target.value })}
                            >
                                <MenuItem value="cash">Cash</MenuItem>
                                <MenuItem value="online">Online</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12}><Divider><Chip label="Fee Details" /></Divider></Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth type="number" label="Tuition Fee"
                                value={formData.tuitionFee}
                                onChange={(e) => setFormData({ ...formData, tuitionFee: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth type="number" label="Term Fee"
                                value={formData.termFee}
                                onChange={(e) => setFormData({ ...formData, termFee: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth type="number" label="Processing/Registration"
                                value={formData.processingFee}
                                onChange={(e) => setFormData({ ...formData, processingFee: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth type="number" label="Admission Fee"
                                value={formData.admissionFee}
                                onChange={(e) => setFormData({ ...formData, admissionFee: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" color="primary">
                                Total: ₹{calculateTotal().toFixed(2)}
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                variant="contained" size="large"
                                startIcon={<AddIcon />}
                                onClick={handleGenerateChallan}
                            >
                                Generate Challan
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            ) : (
                <Box>
                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant="contained" startIcon={<PrintIcon />}
                            onClick={handlePrint} sx={{ mr: 2 }}
                        >
                            Print Challan
                        </Button>
                        <Button variant="outlined" onClick={() => setGeneratedChallan(null)}>
                            New Challan
                        </Button>
                    </Box>

                    {/* Printable Area */}
                    <Paper sx={{ p: 2, overflowX: 'auto' }}>
                        <div ref={printRef}>
                            <div className="challan-container" style={{ display: 'flex' }}>
                                <ChallanCopy title="PDCC Copy" challan={generatedChallan} showDenomination={true} />
                                <ChallanCopy title="Student Copy" challan={generatedChallan} />
                                <ChallanCopy title="Account Department Copy" challan={generatedChallan} />
                                <ChallanCopy title="School Office Copy" challan={generatedChallan} />
                            </div>
                        </div>
                    </Paper>
                </Box>
            )}
        </Box>
    );
};

export default PaymentChallan;
